/**
 * PPA module — centralized data-testid registry.
 * Convention: `ppa_{page}_{element}` or helpers keyed by stable config ids.
 */

// =============================================================================
// Shared utilities
// =============================================================================

export const slugifyTestIdSegment = (value: string): string =>
    value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const slug = slugifyTestIdSegment;

// =============================================================================
// PPA Scope (cross-page — home ribbon, modals, other routes)
// =============================================================================

export const PPA_SCOPE_TEST_ID_PREFIX = 'ppa_scope';
export const PPA_SCOPE_RIBBON = 'ppa_scope_ribbon';
export const PPA_SCOPE_EDIT_BTN = 'ppa_scope_edit_btn';
export const PPA_SCOPE_MODAL = 'ppa_scope_modal';
export const PPA_SCOPE_MODAL_CANCEL_BTN = 'ppa_scope_modal_cancel_btn';
export const PPA_SCOPE_MODAL_APPLY_BTN = 'ppa_scope_modal_apply_btn';

export const ppaScopeFilterGroupTestId = (filterId: string, prefix = PPA_SCOPE_TEST_ID_PREFIX): string =>
    `${prefix}_filter_${filterId}`;
export const ppaScopeFilterOptionTestId = (filterId: string, option: string, prefix = PPA_SCOPE_TEST_ID_PREFIX): string =>
    `${prefix}_option_${filterId}_${slug(option)}`;
export const ppaScopeRibbonValueTestId = (filterId: string, prefix = PPA_SCOPE_TEST_ID_PREFIX): string =>
    `${prefix}_value_${filterId}`;
export const ppaScopeModalTestId = (prefix = PPA_SCOPE_TEST_ID_PREFIX): string => `${prefix}_modal`;
export const ppaScopeModalCancelBtnTestId = (prefix = PPA_SCOPE_TEST_ID_PREFIX): string => `${prefix}_modal_cancel_btn`;
export const ppaScopeModalApplyBtnTestId = (prefix = PPA_SCOPE_TEST_ID_PREFIX): string => `${prefix}_modal_apply_btn`;

// =============================================================================
// Home page / Dashboard (`/ppa/home`)
// =============================================================================

export const PPA_HOME_KPI_TEST_ID_PREFIX = 'ppa_home';
export const PPA_HOME_PAGE = 'ppa_home_page';
export const PPA_HOME_LOADING = 'ppa_home_loading';
export const PPA_HOME_HERO = 'ppa_home_hero';
export const PPA_HOME_KPI_SECTION = 'ppa_home_kpi_section';
export const PPA_HOME_KPI_SKELETON = 'ppa_home_kpi_skeleton';
export const PPA_HOME_ALERTS = 'ppa_home_alerts';
export const PPA_HOME_ACTIVITY = 'ppa_home_activity';
export const PPA_HOME_WORKFLOWS_SECTION = 'ppa_home_workflows_section';
export const PPA_HOME_WORKFLOWS_GRID = 'ppa_home_workflows_grid';
export const PPA_HOME_WORKFLOWS_LOADING = 'ppa_home_workflows_loading';
export const PPA_HOME_FOOTER_PROMPT = 'ppa_home_footer_prompt';

export const ppaKpiCardTestId = (kpiId: string, prefix = PPA_HOME_KPI_TEST_ID_PREFIX): string =>
    `${prefix}_kpi_card_${kpiId}`;
export const ppaKpiCardValueTestId = (kpiId: string, prefix = PPA_HOME_KPI_TEST_ID_PREFIX): string =>
    `${prefix}_kpi_value_${kpiId}`;
export const ppaKpiCardTrendTestId = (kpiId: string, prefix = PPA_HOME_KPI_TEST_ID_PREFIX): string =>
    `${prefix}_kpi_trend_${kpiId}`;
export const ppaWorkflowCardTestId = (workflowId: string): string => `ppa_home_workflow_card_${workflowId}`;
export const ppaWorkflowTitleTestId = (workflowId: string): string => `ppa_home_workflow_title_${workflowId}`;
export const ppaWorkflowIconTestId = (workflowId: string): string => `ppa_home_workflow_icon_${workflowId}`;
export const ppaWorkflowPointTestId = (workflowId: string, pointIndex: number): string =>
    `ppa_home_workflow_point_${workflowId}_${pointIndex}`;
export const ppaWorkflowBtnTestId = (workflowId: string): string => `ppa_home_workflow_btn_${workflowId}`;
export const ppaActivityTabTestId = (tabKey: string): string => `ppa_home_activity_tab_${tabKey}`;

// =============================================================================
// Performance Summary / Opportunity Assessment (`/ppa/opportunity-assessment`)
// =============================================================================

export const PPA_PERF_SUMMARY_TEST_ID_PREFIX = 'ppa_perf_summary';
export const PPA_PERF_SUMMARY_PAGE = 'ppa_perf_summary_page';
export const PPA_PERF_SUMMARY_LOADING = 'ppa_perf_summary_loading';
export const PPA_PERF_SUMMARY_CONTENT = 'ppa_perf_summary_content';
export const PPA_PERF_SUMMARY_BACK_BTN = 'ppa_perf_summary_back_btn';
export const PPA_PERF_SUMMARY_TITLE = 'ppa_perf_summary_title';
export const PPA_PERF_SUMMARY_INFO_BTN = 'ppa_perf_summary_info_btn';
export const PPA_PERF_SUMMARY_EXPORT_BTN = 'ppa_perf_summary_export_btn';
export const PPA_PERF_SUMMARY_KPI_SECTION = 'ppa_perf_summary_kpi_section';
export const PPA_PERF_SUMMARY_OVERALL_SECTION = 'ppa_perf_summary_overall_section';
export const PPA_PERF_SUMMARY_LENS_SECTION = 'ppa_perf_summary_lens_section';
export const PPA_PERF_SUMMARY_SPLIT_RESIZER = 'ppa_perf_summary_split_resizer';
export const PPA_PERF_SUMMARY_EXPORT_MODAL = 'ppa_perf_summary_export_modal';
export const PPA_PERF_SUMMARY_EXPORT_CANCEL_BTN = 'ppa_perf_summary_export_cancel_btn';
export const PPA_PERF_SUMMARY_EXPORT_CONFIRM_BTN = 'ppa_perf_summary_export_confirm_btn';

export type PerfSummarySection = 'overall' | 'lens';

export const ppaPerfSummaryExportOptionTestId = (option: string): string => `ppa_perf_summary_export_option_${option}`;
export const ppaPerfSummarySectionTabTestId = (section: 'overall' | 'lens', tabKey: string): string =>
    `ppa_perf_summary_${section}_tab_${slug(tabKey)}`;
export const ppaPerfSummaryDimensionBtnTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_dimension_btn`;
export const ppaPerfSummaryKpiBtnTestId = (section: PerfSummarySection): string => `ppa_perf_summary_${section}_kpi_btn`;
export const ppaPerfSummaryDimensionSearchTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_dimension_search`;
export const ppaPerfSummaryDimensionOptionTestId = (section: PerfSummarySection, dimensionId: string): string =>
    `ppa_perf_summary_${section}_dimension_option_${slug(dimensionId)}`;
export const ppaPerfSummaryKpiSelectAllTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_kpi_select_all`;
export const ppaPerfSummaryKpiOptionTestId = (section: PerfSummarySection, kpiId: string): string =>
    `ppa_perf_summary_${section}_kpi_option_${slug(kpiId)}`;
export const ppaPerfSummaryKpiResetBtnTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_kpi_reset_btn`;
export const ppaPerfSummaryKpiApplyBtnTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_kpi_apply_btn`;
export const ppaPerfSummarySortAscTestId = (section: PerfSummarySection, columnId: string): string =>
    `ppa_perf_summary_${section}_sort_asc_${slug(columnId)}`;
export const ppaPerfSummarySortDescTestId = (section: PerfSummarySection, columnId: string): string =>
    `ppa_perf_summary_${section}_sort_desc_${slug(columnId)}`;
export const ppaPerfSummaryLensKpiInputTestId = (kpiId: string): string => `ppa_perf_summary_lens_kpi_input_${slug(kpiId)}`;
export const ppaPerfSummaryLensKpiStepUpTestId = (kpiId: string): string => `ppa_perf_summary_lens_kpi_step_up_${slug(kpiId)}`;
export const ppaPerfSummaryLensKpiStepDownTestId = (kpiId: string): string =>
    `ppa_perf_summary_lens_kpi_step_down_${slug(kpiId)}`;

// =============================================================================
// Shared axis / KPI axis configuration (Whitespace + Pack Architecture)
// =============================================================================

export const PPA_AXIS_CONFIG_PANEL = 'ppa_axis_config_panel';
export const PPA_AXIS_CONFIG_APPLY_BTN = 'ppa_axis_config_apply_btn';
export const PPA_AXIS_CONFIG_MODAL = 'ppa_axis_config_modal';
export const PPA_AXIS_CONFIG_MODAL_RESET_BTN = 'ppa_axis_config_modal_reset_btn';
export const PPA_AXIS_CONFIG_MODAL_CANCEL_BTN = 'ppa_axis_config_modal_cancel_btn';
export const PPA_AXIS_CONFIG_MODAL_APPLY_BTN = 'ppa_axis_config_modal_apply_btn';
export const PPA_AXIS_CONFIG_ORDER_ASC = 'ppa_axis_config_order_asc';
export const PPA_AXIS_CONFIG_ORDER_DESC = 'ppa_axis_config_order_desc';
export const PPA_AXIS_CONFIG_SHOW_TOP_INPUT = 'ppa_axis_config_show_top_input';
export const PPA_AXIS_CONFIG_GROUP_REST_CHECKBOX = 'ppa_axis_config_group_rest_checkbox';
export const ppaAxisConfigOptionTestId = (value: string): string => `ppa_axis_config_option_${slug(value)}`;
export const ppaAxisConfigSortByTestId = (sortById: string): string => `ppa_axis_config_sort_by_${slug(sortById)}`;
export const PPA_KPI_AXIS_CONFIG_MODAL = 'ppa_kpi_axis_config_modal';
export const PPA_KPI_AXIS_BINNING_UNIFORM = 'ppa_kpi_axis_binning_uniform';
export const PPA_KPI_AXIS_BINNING_CUSTOM = 'ppa_kpi_axis_binning_custom';
export const PPA_KPI_AXIS_RANGE_MIN = 'ppa_kpi_axis_range_min';
export const PPA_KPI_AXIS_RANGE_MAX = 'ppa_kpi_axis_range_max';
export const PPA_KPI_AXIS_BIN_COUNT = 'ppa_kpi_axis_bin_count';
export const PPA_KPI_AXIS_BIN_SIZE = 'ppa_kpi_axis_bin_size';

// =============================================================================
// Whitespace Analysis (`/ppa/whitespace-analysis`)
// =============================================================================

export const PPA_WHITESPACE_PAGE = 'ppa_whitespace_page';
export const PPA_WHITESPACE_LOADING = 'ppa_whitespace_loading';
export const PPA_WHITESPACE_CONTENT = 'ppa_whitespace_content';
export const PPA_WHITESPACE_BACK_BTN = 'ppa_whitespace_back_btn';
export const PPA_WHITESPACE_TITLE = 'ppa_whitespace_title';
export const PPA_WHITESPACE_INFO_BTN = 'ppa_whitespace_info_btn';
export const PPA_WHITESPACE_EXPORT_BTN = 'ppa_whitespace_export_btn';
export const PPA_WHITESPACE_TOOLBAR = 'ppa_whitespace_toolbar';
export const PPA_WHITESPACE_AXIS_X = 'ppa_whitespace_axis_x';
export const PPA_WHITESPACE_AXIS_X_SETTINGS = 'ppa_whitespace_axis_x_settings';
export const PPA_WHITESPACE_AXIS_Y = 'ppa_whitespace_axis_y';
export const PPA_WHITESPACE_AXIS_Y_SETTINGS = 'ppa_whitespace_axis_y_settings';
export const PPA_WHITESPACE_CROSS_SECTION_BTN = 'ppa_whitespace_cross_section_btn';
export const PPA_WHITESPACE_SHOW_TOGGLE = 'ppa_whitespace_show_toggle';
export const PPA_WHITESPACE_GRID = 'ppa_whitespace_grid';
export const PPA_WHITESPACE_GRID_LOADING = 'ppa_whitespace_grid_loading';
export const PPA_WHITESPACE_GRID_EMPTY = 'ppa_whitespace_grid_empty';
export const PPA_WHITESPACE_NO_DATA = 'ppa_whitespace_no_data';
export const PPA_WHITESPACE_EXPORT_MODAL = 'ppa_whitespace_export_modal';
export const PPA_WHITESPACE_EXPORT_CANCEL_BTN = 'ppa_whitespace_export_cancel_btn';
export const PPA_WHITESPACE_EXPORT_CONFIRM_BTN = 'ppa_whitespace_export_confirm_btn';
export const ppaWhitespaceCrossSectionOptionTestId = (option: string): string =>
    `ppa_whitespace_cross_section_option_${slug(option)}`;
export const ppaWhitespaceExportOptionTestId = (group: 'scope' | 'format', value: string): string =>
    `ppa_whitespace_export_${group}_${slug(value)}`;

// =============================================================================
// Price Pack Architecture (`/ppa/pack-architecture`)
// =============================================================================

export const PPA_PACK_ARCH_PAGE = 'ppa_pack_arch_page';
export const PPA_PACK_ARCH_CONTENT = 'ppa_pack_arch_content';
export const PPA_PACK_ARCH_BACK_BTN = 'ppa_pack_arch_back_btn';
export const PPA_PACK_ARCH_TITLE = 'ppa_pack_arch_title';
export const PPA_PACK_ARCH_INFO_BTN = 'ppa_pack_arch_info_btn';
export const PPA_PACK_ARCH_EXPORT_BTN = 'ppa_pack_arch_export_btn';
export const PPA_PACK_ARCH_TOOLBAR = 'ppa_pack_arch_toolbar';
export const PPA_PACK_ARCH_AXIS_X = 'ppa_pack_arch_axis_x';
export const PPA_PACK_ARCH_AXIS_X_SETTINGS = 'ppa_pack_arch_axis_x_settings';
export const PPA_PACK_ARCH_AXIS_Y = 'ppa_pack_arch_axis_y';
export const PPA_PACK_ARCH_BUBBLE_REPRESENTS = 'ppa_pack_arch_bubble_represents';
export const PPA_PACK_ARCH_BUBBLE_SIZE = 'ppa_pack_arch_bubble_size';
export const PPA_PACK_ARCH_HEATMAP_TOGGLE = 'ppa_pack_arch_heatmap_toggle';
export const PPA_PACK_ARCH_CHART = 'ppa_pack_arch_chart';
export const PPA_PACK_ARCH_CHART_ERROR = 'ppa_pack_arch_chart_error';
export const PPA_PACK_ARCH_CHART_EMPTY = 'ppa_pack_arch_chart_empty';
export const PPA_PACK_ARCH_CHART_LOADING = 'ppa_pack_arch_chart_loading';
export const PPA_PACK_ARCH_LEGEND_EXPAND_BTN = 'ppa_pack_arch_legend_expand_btn';
export const PPA_PACK_ARCH_TABLE = 'ppa_pack_arch_table';
export const PPA_PACK_ARCH_TABLE_SEARCH = 'ppa_pack_arch_table_search';
export const PPA_PACK_ARCH_TABLE_SEARCH_SUBMIT = 'ppa_pack_arch_table_search_submit';
export const PPA_PACK_ARCH_TABLE_EMPTY = 'ppa_pack_arch_table_empty';
export const PPA_PACK_ARCH_NO_DATA = 'ppa_pack_arch_no_data';
export const PPA_PACK_ARCH_EXPORT_MODAL = 'ppa_pack_arch_export_modal';
export const PPA_PACK_ARCH_EXPORT_CANCEL_BTN = 'ppa_pack_arch_export_cancel_btn';
export const PPA_PACK_ARCH_EXPORT_CONFIRM_BTN = 'ppa_pack_arch_export_confirm_btn';
export const ppaPackArchTableKpiTestId = (kpiId: string): string => `ppa_pack_arch_table_kpi_${slug(kpiId)}`;
export const ppaPackArchLegendChipTestId = (segmentId: string): string => `ppa_pack_arch_legend_chip_${slug(segmentId)}`;
export const ppaPackArchExportOptionTestId = (group: 'scope' | 'format', value: string): string =>
    `ppa_pack_arch_export_${group}_${slug(value)}`;

// =============================================================================
// Price Simulator v3 — SIM-01 Scenario Shell & Navigation
// =============================================================================

export const PPA_SIM_TEST_ID_PREFIX = 'ppa_sim';
export const PPA_SIM_PAGE = 'ppa_sim_page';
export const PPA_SIM_LOADING = 'ppa_sim_loading';
export const PPA_SIM_CONTENT = 'ppa_sim_content';
export const PPA_SIM_EMPTY = 'ppa_sim_empty';
export const PPA_SIM_WORKSPACE_LOADING = 'ppa_sim_workspace_loading';
export const PPA_SIM_WORKSPACE = 'ppa_sim_workspace';
export const PPA_SIM_NO_SCOPE = 'ppa_sim_no_scope';
export const PPA_SIM_BREADCRUMB = 'ppa_sim_breadcrumb';
export const PPA_SIM_BREADCRUMB_HOME_LINK = 'ppa_sim_breadcrumb_home_link';
export const PPA_SIM_BREADCRUMB_MANAGER_LINK = 'ppa_sim_breadcrumb_manager_link';
export const PPA_SIM_BREADCRUMB_PROJECT = 'ppa_sim_breadcrumb_project';
export const PPA_SIM_BREADCRUMB_SCENARIO = 'ppa_sim_breadcrumb_scenario';
export const PPA_SIM_SCOPE_BAR = 'ppa_sim_scope_bar';
export const PPA_SIM_SCOPE_TOOLTIP = 'ppa_sim_scope_tooltip';
export const PPA_SIM_SCENARIO_TABS = 'ppa_sim_scenario_tabs';
export const PPA_SIM_SCENARIO_TAB_ADD_BTN = 'ppa_sim_scenario_tab_add_btn';
export const PPA_SIM_SCENARIO_PANEL = 'ppa_sim_scenario_panel';
export const PPA_SIM_TOOLBAR = 'ppa_sim_toolbar';
export const PPA_SIM_SCENARIO_TITLE = 'ppa_sim_scenario_title';
export const PPA_SIM_SCENARIO_RENAME_BTN = 'ppa_sim_scenario_rename_btn';
export const PPA_SIM_SCENARIO_RENAME_INPUT = 'ppa_sim_scenario_rename_input';
export const PPA_SIM_SCENARIO_RENAME_CONFIRM_BTN = 'ppa_sim_scenario_rename_confirm_btn';
export const PPA_SIM_SCENARIO_RENAME_CANCEL_BTN = 'ppa_sim_scenario_rename_cancel_btn';
export const PPA_SIM_SCENARIO_RENAME_ERROR = 'ppa_sim_scenario_rename_error';
export const PPA_SIM_ANALYTICS_BTN = 'ppa_sim_analytics_btn';
export const PPA_SIM_VIEW_BTN = 'ppa_sim_view_btn';
export const PPA_SIM_SEARCH_INPUT = 'ppa_sim_search_input';
export const PPA_SIM_SEARCH_SUBMIT = 'ppa_sim_search_submit';
export const PPA_SIM_SEARCH_EMPTY_RESULT = 'ppa_sim_search_empty_result';
export const PPA_SIM_FILTER_BTN = 'ppa_sim_filter_btn';
export const PPA_SIM_FILTER_PANEL = 'ppa_sim_filter_panel';
export const PPA_SIM_FILTER_SEARCH_INPUT = 'ppa_sim_filter_search_input';
export const PPA_SIM_FILTER_SEARCH_SUBMIT = 'ppa_sim_filter_search_submit';
export const PPA_SIM_FILTER_SHOW_EDITED_TOGGLE = 'ppa_sim_filter_show_edited_toggle';
export const PPA_SIM_FILTER_SHOW_EDITED_BTN = 'ppa_sim_filter_show_edited_btn';
export const PPA_SIM_FILTER_SHOW_IMPACTED_TOGGLE = 'ppa_sim_filter_show_impacted_toggle';
export const PPA_SIM_FILTER_SHOW_IMPACTED_BTN = 'ppa_sim_filter_show_impacted_btn';
export const PPA_SIM_FILTER_RESET_BTN = 'ppa_sim_filter_reset_btn';
export const PPA_SIM_FILTER_APPLY_BTN = 'ppa_sim_filter_apply_btn';
export const PPA_SIM_APPLIED_FILTERS_SECTION = 'ppa_sim_applied_filters_section';
export const PPA_SIM_APPLIED_FILTERS_BAR = 'ppa_sim_applied_filters_bar';
export const PPA_SIM_APPLIED_FILTERS_DETAIL = 'ppa_sim_applied_filters_detail';
export const PPA_SIM_APPLIED_FILTERS_DETAIL_CLOSE_BTN = 'ppa_sim_applied_filters_detail_close_btn';
export const PPA_SIM_APPLIED_FILTERS_DETAIL_CANCEL_BTN = 'ppa_sim_applied_filters_detail_cancel_btn';
export const PPA_SIM_APPLIED_FILTERS_DETAIL_APPLY_BTN = 'ppa_sim_applied_filters_detail_apply_btn';
export const PPA_SIM_APPLIED_FILTERS_SHOW_EDITED_TOGGLE = 'ppa_sim_applied_filters_show_edited_toggle';
export const PPA_SIM_APPLIED_FILTERS_SHOW_IMPACTED_TOGGLE = 'ppa_sim_applied_filters_show_impacted_toggle';
export const PPA_SIM_RESET_BTN = 'ppa_sim_reset_btn';
export const PPA_SIM_EXPORT_BTN = 'ppa_sim_export_btn';
export const PPA_SIM_SIMULATE_BTN = 'ppa_sim_simulate_btn';
export const PPA_SIM_MORE_ACTIONS_BTN = 'ppa_sim_more_actions_btn';
export const PPA_SIM_MORE_ACTIONS_DUPLICATE = 'ppa_sim_more_actions_duplicate';
export const PPA_SIM_MORE_ACTIONS_BULK_UPDATE = 'ppa_sim_more_actions_bulk_update';
export const PPA_SIM_MORE_ACTIONS_DELETE = 'ppa_sim_more_actions_delete';
export const PPA_SIM_DUPLICATE_CONFIRM = 'ppa_sim_duplicate_confirm';
export const PPA_SIM_DUPLICATE_CONFIRM_YES_BTN = 'ppa_sim_duplicate_confirm_yes_btn';
export const PPA_SIM_DUPLICATE_CONFIRM_NO_BTN = 'ppa_sim_duplicate_confirm_no_btn';
export const PPA_SIM_DELETE_CONFIRM = 'ppa_sim_delete_confirm';
export const PPA_SIM_DELETE_CONFIRM_BTN = 'ppa_sim_delete_confirm_btn';
export const PPA_SIM_DELETE_CANCEL_BTN = 'ppa_sim_delete_cancel_btn';
export const PPA_SIM_RESET_MODAL = 'ppa_sim_reset_modal';
export const PPA_SIM_RESET_CONFIRM_BTN = 'ppa_sim_reset_confirm_btn';
export const PPA_SIM_RESET_CANCEL_BTN = 'ppa_sim_reset_cancel_btn';
export const PPA_SIM_CREATE_SCENARIO_MODAL = 'ppa_sim_create_scenario_modal';
export const PPA_SIM_CREATE_SCENARIO_CANCEL_BTN = 'ppa_sim_create_scenario_cancel_btn';
export const PPA_SIM_CREATE_SCENARIO_CONFIRM_BTN = 'ppa_sim_create_scenario_confirm_btn';
export const PPA_SIM_CREATE_SCENARIO_NAME_INPUT = 'ppa_sim_create_scenario_name_input';

export const ppaSimFilterCategoryBtnTestId = (categoryKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_filter_category_${slug(categoryKey)}`;
export const ppaSimFilterCategoryPanelTestId = (categoryKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_filter_category_panel_${slug(categoryKey)}`;
export const ppaSimFilterCategorySearchTestId = (categoryKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_filter_category_search_${slug(categoryKey)}`;
export const ppaSimFilterCategorySelectAllTestId = (categoryKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_filter_category_select_all_${slug(categoryKey)}`;
export const ppaSimFilterCategoryOptionTestId = (
    categoryKey: string,
    optionId: string,
    prefix = PPA_SIM_TEST_ID_PREFIX,
): string => `${prefix}_filter_category_option_${slug(categoryKey)}_${slug(optionId)}`;
export const ppaSimAppliedFiltersGroupTestId = (categoryKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_applied_filters_group_${slug(categoryKey)}`;
export const ppaSimAppliedFiltersDetailTagTestId = (
    categoryKey: string,
    optionId: string,
    prefix = PPA_SIM_TEST_ID_PREFIX,
): string => `${prefix}_applied_filters_tag_${slug(categoryKey)}_${slug(optionId)}`;
export const ppaSimScopeFieldTestId = (fieldLabel: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_scope_${slug(fieldLabel)}`;
export const ppaSimScenarioTabTestId = (scenarioId: string | number, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_scenario_tab_${scenarioId}`;

// =============================================================================
// Price Simulator v3 — Lens tabs & settings dropdown (SIM-03)
// =============================================================================

export const PPA_SIM_LENS_TABS = 'ppa_sim_lens_tabs';
export const PPA_SIM_LENS_LOADING = 'ppa_sim_lens_loading';
export const PPA_SIM_LENS_CONTENT = 'ppa_sim_lens_content';
export const PPA_SIM_LENS_SETTINGS_BTN = 'ppa_sim_lens_settings_btn';
export const PPA_SIM_LENS_SETTINGS_PANEL = 'ppa_sim_lens_settings_panel';
export const PPA_SIM_LENS_SETTINGS_SEARCH_INPUT = 'ppa_sim_lens_settings_search_input';
export const PPA_SIM_LENS_SETTINGS_SEARCH_SUBMIT = 'ppa_sim_lens_settings_search_submit';
export const PPA_SIM_LENS_SETTINGS_LIST = 'ppa_sim_lens_settings_list';
export const PPA_SIM_LENS_CUSTOM_LENS_BTN = 'ppa_sim_lens_custom_lens_btn';
export const PPA_SIM_LENS_CONFIG_BODY = 'ppa_sim_lens_config_body';
export const PPA_SIM_LENS_CONFIG_CONTENT = 'ppa_sim_lens_config_content';
export const PPA_SIM_LENS_CONFIG_SYSTEM_ROW = 'ppa_sim_lens_config_system_row';
export const PPA_SIM_LENS_CONFIG_FOOTER = 'ppa_sim_lens_config_footer';
export const PPA_SIM_LENS_CONFIG_EDIT_BTN = 'ppa_sim_lens_config_edit_btn';
export const PPA_SIM_LENS_CONFIG_RESET_BTN = 'ppa_sim_lens_config_reset_btn';
export const PPA_SIM_LENS_CONFIG_APPLY_BTN = 'ppa_sim_lens_config_apply_btn';

export const ppaSimLensTabTestId = (lensId: string | number, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_lens_tab_${lensId}`;
export const ppaSimLensSettingsItemTestId = (lensId: string | number, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_lens_settings_item_${lensId}`;
export const ppaSimLensSettingsExpandBtnTestId = (lensId: string | number, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_lens_settings_expand_btn_${lensId}`;
export const ppaSimLensConfigPanelTestId = (lensId: string | number, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_lens_config_panel_${lensId}`;
export const ppaSimLensConfigOptionTestId = (attributeKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_lens_config_option_${slug(attributeKey)}`;
export const ppaSimLensConfigColumnTestId = (section: 'dimension' | 'kpi', prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_lens_config_${section}_column`;
export const ppaSimLensConfigSelectAllTestId = (section: 'dimension' | 'kpi', prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_lens_config_select_all_${section}`;

// =============================================================================
// Price Simulator v3 — Custom Lens modal (Create / Edit) (SIM-03)
// =============================================================================

export const PPA_SIM_CUSTOM_LENS_MODAL = 'ppa_sim_custom_lens_modal';
export const PPA_SIM_CUSTOM_LENS_CLOSE_BTN = 'ppa_sim_custom_lens_close_btn';
export const PPA_SIM_CUSTOM_LENS_LOADING = 'ppa_sim_custom_lens_loading';
export const PPA_SIM_CUSTOM_LENS_NAME_INPUT = 'ppa_sim_custom_lens_name_input';
export const PPA_SIM_CUSTOM_LENS_NAME_COUNTER = 'ppa_sim_custom_lens_name_counter';
export const PPA_SIM_CUSTOM_LENS_NAME_CLEAR_BTN = 'ppa_sim_custom_lens_name_clear_btn';
export const PPA_SIM_CUSTOM_LENS_NAME_ERROR = 'ppa_sim_custom_lens_name_error';
export const PPA_SIM_CUSTOM_LENS_AI_TOGGLE = 'ppa_sim_custom_lens_ai_toggle';
export const PPA_SIM_CUSTOM_LENS_SELECTION_PANEL = 'ppa_sim_custom_lens_selection_panel';
export const PPA_SIM_CUSTOM_LENS_PREVIEW_EMPTY = 'ppa_sim_custom_lens_preview_empty';
export const PPA_SIM_CUSTOM_LENS_CANCEL_BTN = 'ppa_sim_custom_lens_cancel_btn';
export const PPA_SIM_CUSTOM_LENS_SAVE_BTN = 'ppa_sim_custom_lens_save_btn';
export const PPA_SIM_CUSTOM_LENS_DELETE_BTN = 'ppa_sim_custom_lens_delete_btn';
export const PPA_SIM_CUSTOM_LENS_EDIT_WARNING = 'ppa_sim_custom_lens_edit_warning';
export const PPA_SIM_CUSTOM_LENS_DELETE_MODAL = 'ppa_sim_custom_lens_delete_modal';
export const PPA_SIM_CUSTOM_LENS_DELETE_CONFIRM_BTN = 'ppa_sim_custom_lens_delete_confirm_btn';
export const PPA_SIM_CUSTOM_LENS_DELETE_CANCEL_BTN = 'ppa_sim_custom_lens_delete_cancel_btn';

export const ppaSimCustomLensFieldBtnTestId = (fieldKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_custom_lens_field_${slug(fieldKey)}_btn`;
export const ppaSimCustomLensFieldSearchTestId = (fieldKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_custom_lens_field_${slug(fieldKey)}_search`;
export const ppaSimCustomLensFieldOptionTestId = (
    fieldKey: string,
    optionId: string,
    prefix = PPA_SIM_TEST_ID_PREFIX,
): string => `${prefix}_custom_lens_field_${slug(fieldKey)}_option_${slug(optionId)}`;
export const ppaSimCustomLensFieldAddBtnTestId = (fieldKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_custom_lens_field_${slug(fieldKey)}_add_btn`;
export const ppaSimCustomLensPreviewPanelTestId = (section: 'dimension' | 'kpi', prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_custom_lens_preview_${section}_panel`;
export const ppaSimCustomLensPreviewItemTestId = (
    section: 'dimension' | 'kpi',
    item: string,
    prefix = PPA_SIM_TEST_ID_PREFIX,
): string => `${prefix}_custom_lens_preview_${section}_item_${slug(item)}`;
export const ppaSimCustomLensPreviewRemoveBtnTestId = (
    section: 'dimension' | 'kpi',
    item: string,
    prefix = PPA_SIM_TEST_ID_PREFIX,
): string => `${prefix}_custom_lens_preview_${section}_remove_${slug(item)}`;

// =============================================================================
// Price Simulator v3 — Scenario lens table (SIM-03)
// =============================================================================

export const PPA_SIM_TABLE = 'ppa_sim_table';
export const PPA_SIM_TABLE_LOADING = 'ppa_sim_table_loading';
export const PPA_SIM_TABLE_EMPTY = 'ppa_sim_table_empty';
export const PPA_SIM_TABLE_FOOTER = 'ppa_sim_table_footer';
export const PPA_SIM_TABLE_ADD_ROW_BTN = 'ppa_sim_table_add_row_btn';
export const PPA_SIM_TABLE_AI_HEADER = 'ppa_sim_table_ai_header';
export const PPA_SIM_TABLE_AI_INFO_BTN = 'ppa_sim_table_ai_info_btn';
export const PPA_SIM_TABLE_SELECT_ALL = 'ppa_sim_table_select_all';

const simCol = (columnKey: string, suffix: string, prefix = PPA_SIM_TEST_ID_PREFIX) =>
    `${prefix}_table_col_${slug(columnKey)}_${suffix}`;
const simColFilter = (columnKey: string, suffix: string, prefix = PPA_SIM_TEST_ID_PREFIX) =>
    `${prefix}_table_col_filter_${suffix}_${slug(columnKey)}`;
const simKpi = (groupKey: string, suffix: string, prefix = PPA_SIM_TEST_ID_PREFIX) =>
    `${prefix}_table_kpi_${slug(groupKey)}_${suffix}`;

export const ppaSimTableRowSelectTestId = (rowKey: string | number, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    `${prefix}_table_row_select_${slug(String(rowKey))}`;
export const ppaSimTableColumnHeaderTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simCol(columnKey, 'header', prefix);
export const ppaSimTableColumnSortBtnTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simCol(columnKey, 'sort_btn', prefix);
export const ppaSimTableColumnFilterBtnTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simCol(columnKey, 'filter_btn', prefix);
export const ppaSimTableColumnSelectAllTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simCol(columnKey, 'select_all', prefix);
export const ppaSimTableColFilterPanelTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simColFilter(columnKey, 'panel', prefix);
export const ppaSimTableColFilterSearchTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simColFilter(columnKey, 'search', prefix);
export const ppaSimTableColFilterSearchSubmitTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simColFilter(columnKey, 'search_submit', prefix);
export const ppaSimTableColFilterSortAscTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simColFilter(columnKey, 'sort_asc', prefix);
export const ppaSimTableColFilterSortDescTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simColFilter(columnKey, 'sort_desc', prefix);
export const ppaSimTableColFilterSelectAllTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simColFilter(columnKey, 'select_all', prefix);
export const ppaSimTableColFilterOptionTestId = (
    columnKey: string,
    option: string,
    prefix = PPA_SIM_TEST_ID_PREFIX,
): string => `${prefix}_table_col_filter_option_${slug(columnKey)}_${slug(option)}`;
export const ppaSimTableColFilterResetBtnTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simColFilter(columnKey, 'reset_btn', prefix);
export const ppaSimTableColFilterCancelBtnTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simColFilter(columnKey, 'cancel_btn', prefix);
export const ppaSimTableColFilterApplyBtnTestId = (columnKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simColFilter(columnKey, 'apply_btn', prefix);
export const ppaSimTableKpiGroupHeaderTestId = (groupKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simKpi(groupKey, 'header', prefix);
export const ppaSimTableKpiSortBtnTestId = (groupKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simKpi(groupKey, 'sort_btn', prefix);
export const ppaSimTableKpiSortPanelTestId = (groupKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simKpi(groupKey, 'sort_panel', prefix);
export const ppaSimTableKpiSortDirectionTestId = (groupKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simKpi(groupKey, 'sort_direction', prefix);
export const ppaSimTableKpiSortOptionTestId = (
    groupKey: string,
    optionKey: string,
    prefix = PPA_SIM_TEST_ID_PREFIX,
): string => `${prefix}_table_kpi_${slug(groupKey)}_sort_option_${slug(optionKey)}`;
export const ppaSimTableKpiSortCancelBtnTestId = (groupKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simKpi(groupKey, 'sort_cancel_btn', prefix);
export const ppaSimTableKpiSortApplyBtnTestId = (groupKey: string, prefix = PPA_SIM_TEST_ID_PREFIX): string =>
    simKpi(groupKey, 'sort_apply_btn', prefix);
